/**
 * Sticky global filter bar (spec §101).
 *
 * Dropdowns cascade: VP constrains manager, manager constrains employee. The
 * store clears orphaned selections, so the UI can never hold a combination the
 * backend would reject.
 */
import { useState } from "react";
import { CalendarDays, RotateCcw, SlidersHorizontal } from "lucide-react";

import { useFilterOptions } from "@/hooks/useAnalytics";
import { type PeriodKey, useFilterStore } from "@/store/filters";

const PERIODS: Array<{ value: PeriodKey; label: string }> = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7", label: "Last 7 days" },
  { value: "last_30", label: "Last 30 days" },
  { value: "mtd", label: "MTD" },
  { value: "previous_mtd", label: "Previous MTD" },
  { value: "qtd", label: "QTD (financial)" },
  { value: "previous_quarter", label: "Previous quarter" },
  { value: "fytd", label: "FYTD (Apr–Mar)" },
  { value: "previous_fytd", label: "Previous FYTD" },
  { value: "custom", label: "Custom…" },
];

function Select({
  label, value, onChange, options, placeholder = "All",
}: {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: Array<{ value: string; label: string; needs_review?: boolean }>;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1 min-w-[132px] flex-1 sm:flex-none">
      <span className="label">{label}</span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="text-sm bg-surface border border-rule rounded-md px-2 py-1.5
                   focus:outline-none focus:ring-2 focus:ring-petrol/30"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
            {option.needs_review ? " ⚠" : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

export function FilterBar() {
  const filters = useFilterStore();
  const vps = useFilterOptions("vps");
  const managers = useFilterOptions("managers", filters.vp);
  const employees = useFilterOptions("employees", filters.manager);
  const areas = useFilterOptions("areas");

  // Five stacked dropdowns eat most of a phone screen before any data shows.
  // Period stays visible because it is the one people change constantly; the
  // scope filters fold behind a toggle that says how many are active.
  const [open, setOpen] = useState(false);
  const activeCount = [filters.vp, filters.manager, filters.employee, filters.area]
    .filter(Boolean).length;

  return (
    <div className="sticky top-0 z-30 bg-ground/90 backdrop-blur border-b border-rule
                    shrink-0">
      <div className="px-3 sm:px-4 lg:px-5 py-2.5 lg:py-3
                      flex flex-wrap items-end gap-2 lg:gap-3">
        <label className="flex flex-col gap-1 min-w-[150px] flex-1 sm:flex-none">
          <span className="label">Period</span>
          <div className="relative">
            <CalendarDays size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-faint" />
            <select
              value={filters.period}
              onChange={(e) => filters.set({ period: e.target.value as PeriodKey })}
              className="w-full text-sm bg-surface border border-rule rounded-md pl-7 pr-2 py-1.5
                         focus:outline-none focus:ring-2 focus:ring-petrol/30"
            >
              {PERIODS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        </label>

        {filters.period === "custom" && (
          <>
            <label className="flex flex-col gap-1">
              <span className="label">From</span>
              <input
                type="date" value={filters.dateFrom ?? ""}
                onChange={(e) => filters.set({ dateFrom: e.target.value || null })}
                className="text-sm bg-surface border border-rule rounded-md px-2 py-1.5"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="label">To</span>
              <input
                type="date" value={filters.dateTo ?? ""}
                onChange={(e) => filters.set({ dateTo: e.target.value || null })}
                className="text-sm bg-surface border border-rule rounded-md px-2 py-1.5"
              />
            </label>
          </>
        )}

        {/* Toggle is phone-only; from sm the filters are always shown. */}
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="btn text-xs sm:hidden h-[34px]"
        >
          <SlidersHorizontal size={13} />
          Filters{activeCount ? ` (${activeCount})` : ""}
        </button>

        <div className={`${open ? "flex" : "hidden"} sm:flex flex-wrap items-end
                         gap-2 lg:gap-3 w-full sm:w-auto`}>
        <Select label="VP" value={filters.vp} onChange={(v) => filters.set({ vp: v })}
                options={vps.data ?? []} />
        <Select label="Manager" value={filters.manager} onChange={(v) => filters.set({ manager: v })}
                options={managers.data ?? []}
                placeholder={filters.vp ? "All under VP" : "All"} />
        <Select label="Employee" value={filters.employee} onChange={(v) => filters.set({ employee: v })}
                options={employees.data ?? []}
                placeholder={filters.manager ? "All in team" : "All"} />
        <Select label="Area" value={filters.area} onChange={(v) => filters.set({ area: v })}
                options={areas.data ?? []} />
        </div>

        <button className="btn text-xs ml-auto h-[34px]" onClick={filters.reset}>
          <RotateCcw size={13} /> Reset
        </button>
      </div>
    </div>
  );
}
